import { useQuery } from "@apollo/client";
import Button from "components/base/Button";
import Errors from "components/base/Errors";
import { InlineSpinner } from "components/base/Spinner";
import { useModals } from "ModalProvider";
import CustomerInvoicesView from "./CustomerInvoicesView";
import { FETCH_CUSTOMER_PAYMENT_TIME } from "./graphql";

function parsePaidInvoices(value) {
  if (!value) return [];
  if (Array.isArray(value)) return value;
  if (typeof value !== "string") return [];
  try {
    const parsed = JSON.parse(value);
    return Array.isArray(parsed?.records) ? parsed.records : [];
  } catch {
    return [];
  }
}

function getDateOnly(value) {
  return String(value || "").split(" ")[0];
}

function getPaymentDelayDays(invoice) {
  const dueDate = getDateOnly(invoice?.invoice_date_due);
  const paymentDate = getDateOnly(invoice?.x_payment_date);
  if (!dueDate || !paymentDate) return null;

  const due = new Date(`${dueDate}T00:00:00`);
  const paid = new Date(`${paymentDate}T00:00:00`);
  if (Number.isNaN(due.getTime()) || Number.isNaN(paid.getTime())) return null;

  const diffMs = paid.getTime() - due.getTime();
  return Math.round(diffMs / (1000 * 60 * 60 * 24));
}

const CustomerPaymentTimeView = ({ partnerId, startYear, endYear }) => {
  const modal = useModals();

  const currentYear = new Date().getFullYear();
  const start = Number(startYear) || currentYear - 1;
  const end = Number(endYear) || currentYear;
  const { loading, error, data } = useQuery(FETCH_CUSTOMER_PAYMENT_TIME, {
    variables: {
      partnerId: Number(partnerId),
      startYear: Math.min(start, end),
      endYear: Math.max(start, end),
    },
    skip: !partnerId,
    fetchPolicy: "network-only",
  });

  const records = parsePaidInvoices(data?.odooCustomerPaidInvoices);
  const delayDays = records
    .map((invoice) => getPaymentDelayDays(invoice))
    .filter((days) => typeof days === "number");
  const avgDelayDays =
    delayDays.length > 0
      ? delayDays.reduce((sum, days) => sum + days, 0) / delayDays.length
      : null;

  function showInvoices() {
    modal.present({
      title: "Customer Invoices",
      subtitle: `Invoices paid between ${start} and ${end}`,
      maxWidth: "max-w-5xl",
      children: <CustomerInvoicesView invoices={records} />,
    });
  }

  return (
    <div className="card">
      <h5>Payment Time</h5>
      <div className="mt-2">
        {loading ? (
          <InlineSpinner size={18} />
        ) : error ? (
          <Errors error={error} />
        ) : (
          <div className="space-y-2">
            <div>
              Avg payment days:{" "}
              {avgDelayDays === null ? "-" : avgDelayDays.toFixed(1)} days
            </div>
            <div>
              <Button onClick={showInvoices}>
                {delayDays.length} Invoices
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CustomerPaymentTimeView;
